import { useState } from 'react'
import { oneOf } from 'prop-types'
import { PropTypes } from 'mobx-react'
import i18next from 'common-ui-components/src/vtj/i18next'
import { SUPPORTED_LANGUAGES } from 'common/src/vtj/browser/localization'
import SupportedBrowsers from 'todistukset-ui/src/vtj/server/supported-browsers.enum'
import Icon from 'common-ui-components/src/vtj/icon/Icon.react'
import colors from 'common-ui-components/src/vtj/theme/colors'
import variables from 'common-ui-components/src/vtj/theme/variables'
import ExternalLink from 'todistukset-ui/src/vtj/components/unsupported-browser-banner/ExternalLink.react'
import {
  UnsupportedBrowserBannerWrapper,
  UnsupportedBrowserBannerContent,
  DescriptionWrapper,
  CloseButton,
  CloseText,
  MessageContent,
  DescriptionText,
  BrowserLinks,
  BrowserLinkItem,
} from 'todistukset-ui/src/vtj/components/unsupported-browser-banner/UnsupportedBrowserBanner.sc'

const UnsupportedBrowserBanner = ({ browserUpdateRecommendations, lang }) => {
  const [isClosed, setClosed] = useState(false)
  const translate = i18next.getFixedT(lang)
  const { fontSize } = variables
  const { textLightColor } = colors
  const closeIconSize = `${fontSize}px`

  const closeBanner = (e) => {
    e.preventDefault()
    setClosed(true)
  }

  const shouldShowBanner = browserUpdateRecommendations.length > 0 && !isClosed
  if (!shouldShowBanner) {
    return null
  }

  return (
    <UnsupportedBrowserBannerWrapper data-test-id="unsupported-browser-banner">
      <UnsupportedBrowserBannerContent>
        <MessageContentWrapper
          browserUpdateRecommendations={browserUpdateRecommendations}
          translate={translate}
          lang={lang}
        />
        <CloseButton
          onClick={closeBanner}
          aria-label={translate('closeBanner')}
          data-test-id="close-unsupported-banner-button"
        >
          <CloseText aria-hidden>{translate('closeBanner')}</CloseText>
          <Icon
            iconId="suomifi-icon-cross"
            height={closeIconSize}
            width={closeIconSize}
            iconColor={textLightColor}
          />
        </CloseButton>
      </UnsupportedBrowserBannerContent>
    </UnsupportedBrowserBannerWrapper>
  )
}

const MessageContentWrapper = ({
  browserUpdateRecommendations,
  translate,
  lang,
}) => {
  const { infoBlue } = colors
  const infoIconSize = '24px'
  return (
    <MessageContent>
      <DescriptionWrapper>
        <Icon
          iconId="suomifi-icon-info"
          height={infoIconSize}
          width={infoIconSize}
          iconColor={infoBlue}
        />
        <DescriptionText>
          {translate('unsupportedBrowserDescription')}
        </DescriptionText>
      </DescriptionWrapper>
      <BrowserLinks>
        {browserUpdateRecommendations.map((browser) => (
          <BrowserItem
            key={browser}
            browser={browser}
            lang={lang}
            translate={translate}
          />
        ))}
      </BrowserLinks>
    </MessageContent>
  )
}

const BrowserItem = ({ browser, lang, translate }) => {
  const browserName = translate(`${browser}_name`)
  return (
    <BrowserLinkItem key={browser} data-test-id={`${browser}-item`}>
      <ExternalLink
        data-test-id={`${browser}-link`}
        lang={lang}
        href={translate(`${browser}_link`)}
        aria-label={`${translate('downloadOrUpdate')} ${browserName}`}
      >
        {browserName}
      </ExternalLink>
    </BrowserLinkItem>
  )
}

UnsupportedBrowserBanner.displayName = 'UnsupportedBrowserBanner'

UnsupportedBrowserBanner.propTypes = {
  lang: oneOf(SUPPORTED_LANGUAGES).isRequired,
  browserUpdateRecommendations: PropTypes.arrayOrObservableArrayOf(
    oneOf(Object.values(SupportedBrowsers))
  ).isRequired,
}

UnsupportedBrowserBanner.defaultProps = {
  browserUpdateRecommendations: [],
}

/** @component */
export default UnsupportedBrowserBanner
